/**
 * Atomic Registration Service
 *
 * Creates Firebase Auth user AND Firestore team/membership documents atomically.
 * If Firestore creation fails, the Auth user is deleted to prevent "dangling" accounts.
 *
 * This ensures users cannot exist in a state where:
 * - They are authenticated, but
 * - They do not have a team/company + membership record needed for admin approval
 */

import {
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  type User,
} from 'firebase/auth';
import { getEmailVerificationActionCodeSettings } from '@/lib/auth/emailVerificationConfig';
import { doc, serverTimestamp, collection, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { defaultTeamOnboarding } from '@/types/onboarding';
import { ENTITLEMENT_SCHEMA_VERSION } from '@/lib/entitlement';
// Note: Membership index is written directly in batch, not via upsertMembershipIndex helper
// NOTE: seedSampleData is now called from WelcomeOnboardingPage after businessType is selected
// This ensures sample data respects the user's business type choice

export interface AtomicRegistrationInput {
  // Account - split name fields
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  password: string;

  // Company (only required fields)
  companyName: string;
  phone: string;

  // Acquisition tracking (required)
  referralSource: string;
  // Only required when referralSource === 'other'
  referralOther?: string;
}

export interface InviteOnlyRegistrationInput {
  // Account only - no company fields, split name fields
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  password: string;
  // Invite info
  inviteId: string;
  teamId: string;

  // Acquisition tracking (required)
  referralSource: string;
  // Only required when referralSource === 'other'
  referralOther?: string;
}

export interface AtomicRegistrationResult {
  success: boolean;
  userId?: string;
  teamId?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Generate a URL-safe slug from company name
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
}

/**
 * Generate a unique team ID
 */
function generateTeamId(): string {
  return doc(collection(db, 'teams')).id;
}

/**
 * Log registration error to Firestore for debugging
 * This is best-effort and should not block the error flow
 */
async function logRegistrationError(
  email: string,
  error: unknown,
  phase: 'auth' | 'firestore' | 'rollback'
): Promise<void> {
  try {
    const errorDoc = doc(collection(db, 'onboardingErrors'));
    await writeBatch(db)
      .set(errorDoc, {
        email,
        phase,
        error: error instanceof Error ? error.message : String(error),
        errorCode: error && typeof error === 'object' && 'code' in error ? error.code : null,
        timestamp: serverTimestamp(),
      })
      .commit();
  } catch (logError) {
    // Silently fail - logging should not block the main flow
    logger.warn('[AtomicRegistration] Failed to log error:', logError);
  }
}

/**
 * Create Firebase Auth user and all required Firestore documents atomically.
 *
 * Flow:
 * 1. Create Firebase Auth user
 * 2. Update user profile with display name
 * 3. Create all Firestore documents in a batch:
 *    - /users/{uid} (user profile)
 *    - /teams/{teamId} (team with canonical entitlement)
 *    - /teams/{teamId}/members/{uid} (membership with role: 'owner')
 *    - /teams/{teamId}/settings/companyBranding
 *    - /teams/{teamId}/settings/ui
 *    - /teams/{teamId}/settings/onboarding
 * 4. If Firestore fails, delete the Auth user (rollback)
 *
 * @param input - Registration data (account + company)
 * @returns Result with success status, IDs, or error details
 */
export async function atomicRegistration(
  input: AtomicRegistrationInput
): Promise<AtomicRegistrationResult> {
  let authUser: User | null = null;
  const teamId = generateTeamId();
  const slug = generateSlug(input.companyName);

  try {
    // Step 1: Create Firebase Auth user
    logger.info('[AtomicRegistration] Creating Auth user...');
    const userCredential = await createUserWithEmailAndPassword(auth, input.email, input.password);
    authUser = userCredential.user;

    // Step 2: Update profile with display name (fullName for Firebase Auth)
    await updateProfile(authUser, { displayName: input.fullName });
    logger.info('[AtomicRegistration] Auth user created:', authUser.uid);

    // Step 3: Create all Firestore documents in a batch
    logger.info('[AtomicRegistration] Creating Firestore documents...');
    const batch = writeBatch(db);

    // 3a. Create team document
    const teamRef = doc(db, 'teams', teamId);
    batch.set(teamRef, {
      id: teamId,
      name: input.companyName,
      slug,
      settings: {
        currency: 'EUR',
        locale: 'nl-NL',
        timezone: 'Europe/Amsterdam',
      },
      // Canonical entitlement object - SINGLE SOURCE OF TRUTH
      entitlement: {
        tier: 'free',
        state: 'free',
        activatedAt: serverTimestamp(),
        source: 'signup',
        schemaVersion: ENTITLEMENT_SCHEMA_VERSION,
      },
      entitlementSchemaVersion: ENTITLEMENT_SCHEMA_VERSION,
      // Company details for admin review
      phone: input.phone,
      createdBy: input.email,
      // Acquisition tracking for analytics
      acquisitionSource: input.referralSource,
      acquisitionSourceNote: input.referralOther || null,
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // 3b. Membership doc + index are written by bootstrapTeamOwnerMembershipCallable CF
    // after the batch commits (INV-MEMBER-1, INV-IDX-1 — Admin SDK only)

    // 3c. Create/update user profile with activeTeamId and safety flags
    const userRef = doc(db, 'users', authUser.uid);
    batch.set(
      userRef,
      {
        activeTeamId: teamId,
        email: input.email,
        displayName: input.fullName, // Legacy field for backward compatibility
        // Safety flags to prevent duplicate team creation
        initialTeamId: teamId,
        initialTeamCreatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // 3c2. Create user profile/data subcollection (source of truth for names)
    const profileDataRef = doc(db, 'users', authUser.uid, 'profile', 'data');
    batch.set(profileDataRef, {
      firstName: input.firstName,
      lastName: input.lastName,
      fullName: input.fullName,
      updatedAt: serverTimestamp(),
    });

    // 3d. Initialize company branding settings
    const brandingRef = doc(db, 'teams', teamId, 'settings', 'companyBranding');
    batch.set(brandingRef, {
      companyName: input.companyName,
      phone: input.phone,
      address: '',
      kvkNumber: '',
      vatNumber: '',
      logoUrl: '',
      updatedAt: serverTimestamp(),
    });

    // 3e. Initialize UI settings
    const uiRef = doc(db, 'teams', teamId, 'settings', 'ui');
    batch.set(uiRef, {
      language: 'nl',
      dateFormat: 'DD-MM-YYYY',
      updatedAt: serverTimestamp(),
    });

    // 3f. Initialize onboarding checklist
    const onboardingRef = doc(db, 'teams', teamId, 'settings', 'onboarding');
    batch.set(onboardingRef, {
      ...defaultTeamOnboarding,
      createdAt: serverTimestamp(),
    });

    // Commit all Firestore writes atomically
    await batch.commit();

    // Bootstrap owner membership via CF (Admin SDK — INV-MEMBER-1, INV-IDX-1)
    const fns = getFunctions(undefined, 'europe-west1');
    const bootstrapMembership = httpsCallable<
      { teamId: string; displayName?: string; teamName?: string },
      { ok: boolean; created: boolean }
    >(fns, 'bootstrapTeamOwnerMembershipCallable');
    await bootstrapMembership({
      teamId,
      displayName: input.fullName,
      teamName: input.companyName,
    });

    // CRITICAL: Force token refresh to pick up custom claims set by CF
    // The bootstrapMembership CF sets custom claims (activeTeamId, teamRole, etc.)
    // but the client's cached token doesn't have them yet. Without this refresh,
    // the user will get permission-denied errors when accessing team resources.
    try {
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        logger.info('[AtomicRegistration] Token refreshed with custom claims', {
          userId: authUser.uid,
          teamId,
        });
      }
    } catch (tokenErr) {
      logger.warn('[AtomicRegistration] Token refresh failed (non-critical):', tokenErr);
    }

    logger.info('[AtomicRegistration] Successfully created account + team:', {
      userId: authUser.uid,
      teamId,
      companyName: input.companyName,
    });

    // Step 4: Send email verification (non-blocking)
    // This is critical for anti-abuse but should not block registration
    sendEmailVerification(authUser, getEmailVerificationActionCodeSettings()).catch(
      (verifyError) => {
        logger.warn(
          '[AtomicRegistration] Email verification send failed (non-critical):',
          verifyError
        );
      }
    );

    // NOTE: Sample data seeding is now done in WelcomeOnboardingPage.completeOnboarding()
    // This ensures sample data respects the user's businessType selection
    // The seedSampleData function is idempotent, so it's safe to call it there

    return {
      success: true,
      userId: authUser.uid,
      teamId,
    };
  } catch (error) {
    logger.error('[AtomicRegistration] Registration failed:', error);

    // Determine error phase
    const isAuthError = !authUser;
    const phase = isAuthError ? 'auth' : 'firestore';

    // Log error for debugging
    void logRegistrationError(input.email, error, phase);

    // Step 4: Rollback - delete Auth user if Firestore failed
    if (authUser) {
      logger.info('[AtomicRegistration] Rolling back Auth user...');
      try {
        await deleteUser(authUser);
        logger.info('[AtomicRegistration] Auth user deleted (rollback successful)');
      } catch (rollbackError) {
        // This is a critical error - we have a dangling Auth user
        logger.error('[AtomicRegistration] CRITICAL: Failed to delete Auth user:', rollbackError);
        void logRegistrationError(input.email, rollbackError, 'rollback');

        // Return a specific error so the UI can guide the user
        // Error message key: errors.registration.rollbackFailed
        return {
          success: false,
          error: 'errors.registration.rollbackFailed',
          errorCode: 'ROLLBACK_FAILED',
        };
      }
    }

    // Map Firebase Auth errors to i18n translation keys
    // These keys are defined in common.json under errors.registration.*
    let errorMessageKey = 'errors.registration.unknown';
    let errorCode = 'UNKNOWN';

    if (error && typeof error === 'object' && 'code' in error) {
      const code = error.code as string;
      errorCode = code;

      switch (code) {
        case 'auth/email-already-in-use':
          // Try to detect which provider is already linked
          try {
            const methods = await fetchSignInMethodsForEmail(auth, input.email);
            if (methods.includes('google.com') && methods.includes('microsoft.com')) {
              errorMessageKey = 'errors.registration.emailAlreadyInUseGoogleAndMicrosoft';
            } else if (methods.includes('google.com')) {
              errorMessageKey = 'errors.registration.emailAlreadyInUseGoogle';
            } else if (methods.includes('microsoft.com')) {
              errorMessageKey = 'errors.registration.emailAlreadyInUseMicrosoft';
            } else {
              errorMessageKey = 'errors.registration.emailAlreadyInUse';
            }
          } catch {
            // Fallback to generic message if provider detection fails
            errorMessageKey = 'errors.registration.emailAlreadyInUse';
          }
          break;
        case 'auth/weak-password':
          errorMessageKey = 'errors.registration.weakPassword';
          break;
        case 'auth/invalid-email':
          errorMessageKey = 'errors.registration.invalidEmail';
          break;
        case 'auth/operation-not-allowed':
          errorMessageKey = 'errors.registration.operationNotAllowed';
          break;
        case 'auth/network-request-failed':
          errorMessageKey = 'errors.registration.networkError';
          break;
        case 'permission-denied':
          errorMessageKey = 'errors.registration.permissionDenied';
          break;
        default:
          if (code.startsWith('auth/')) {
            errorMessageKey = 'errors.registration.authError';
          }
      }
    }

    return {
      success: false,
      error: errorMessageKey,
      errorCode,
    };
  }
}

/**
 * Invite-only registration - creates user WITHOUT team
 *
 * Used when user signs up via an invite link.
 * Does NOT create a team - user will join existing team via invite acceptance.
 *
 * Flow:
 * 1. Create Firebase Auth user
 * 2. Update user profile with display name
 * 3. Create minimal user profile in Firestore with onboardingState: 'invited'
 * 4. If Firestore fails, delete the Auth user (rollback)
 *
 * @param input - Registration data (account only, no company)
 * @returns Result with success status, userId, or error details
 */
export async function inviteOnlyRegistration(
  input: InviteOnlyRegistrationInput
): Promise<AtomicRegistrationResult> {
  let authUser: User | null = null;

  try {
    // Step 1: Create Firebase Auth user
    logger.info('[InviteOnlyRegistration] Creating Auth user for invite flow...');
    const userCredential = await createUserWithEmailAndPassword(auth, input.email, input.password);
    authUser = userCredential.user;

    // Step 2: Update profile with display name (fullName for Firebase Auth)
    await updateProfile(authUser, { displayName: input.fullName });
    logger.info('[InviteOnlyRegistration] Auth user created:', authUser.uid);

    // Step 3: Create minimal user profile (NO team creation)
    logger.info('[InviteOnlyRegistration] Creating user profile (no team)...');
    const batch = writeBatch(db);

    // Create user profile with invited state
    const userRef = doc(db, 'users', authUser.uid);
    batch.set(
      userRef,
      {
        email: input.email,
        displayName: input.fullName, // Legacy field for backward compatibility
        // Mark as invited user - no team yet
        onboardingState: 'invited',
        pendingInvite: {
          inviteId: input.inviteId,
          teamId: input.teamId,
        },
        // No activeTeamId - will be set when invite is accepted
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // Create user profile/data subcollection (source of truth for names)
    // Note: referralSource is stored on the user profile for invite flow since there's no team creation
    const profileDataRef = doc(db, 'users', authUser.uid, 'profile', 'data');
    batch.set(profileDataRef, {
      firstName: input.firstName,
      lastName: input.lastName,
      fullName: input.fullName,
      referralSource: input.referralSource,
      referralSourceNote: input.referralOther || null,
      updatedAt: serverTimestamp(),
    });

    // Commit user profile
    await batch.commit();

    logger.info('[InviteOnlyRegistration] Successfully created invited user:', {
      userId: authUser.uid,
      inviteId: input.inviteId,
      teamId: input.teamId,
    });

    return {
      success: true,
      userId: authUser.uid,
    };
  } catch (error) {
    logger.error('[InviteOnlyRegistration] Registration failed:', error);

    // Determine error phase
    const isAuthError = !authUser;
    const phase = isAuthError ? 'auth' : 'firestore';

    // Log error for debugging
    void logRegistrationError(input.email, error, phase);

    // Step 4: Rollback - delete Auth user if Firestore failed
    if (authUser) {
      logger.info('[InviteOnlyRegistration] Rolling back Auth user...');
      try {
        await deleteUser(authUser);
        logger.info('[InviteOnlyRegistration] Auth user deleted (rollback successful)');
      } catch (rollbackError) {
        logger.error(
          '[InviteOnlyRegistration] CRITICAL: Failed to delete Auth user:',
          rollbackError
        );
        void logRegistrationError(input.email, rollbackError, 'rollback');
        return {
          success: false,
          error: 'errors.registration.rollbackFailed',
          errorCode: 'ROLLBACK_FAILED',
        };
      }
    }

    // Map Firebase Auth errors to i18n translation keys
    let errorMessageKey = 'errors.registration.unknown';
    let errorCode = 'UNKNOWN';

    if (error && typeof error === 'object' && 'code' in error) {
      const code = error.code as string;
      errorCode = code;

      switch (code) {
        case 'auth/email-already-in-use':
          // Try to detect which provider is already linked
          try {
            const methods = await fetchSignInMethodsForEmail(auth, input.email);
            if (methods.includes('google.com') && methods.includes('microsoft.com')) {
              errorMessageKey = 'errors.registration.emailAlreadyInUseGoogleAndMicrosoft';
            } else if (methods.includes('google.com')) {
              errorMessageKey = 'errors.registration.emailAlreadyInUseGoogle';
            } else if (methods.includes('microsoft.com')) {
              errorMessageKey = 'errors.registration.emailAlreadyInUseMicrosoft';
            } else {
              errorMessageKey = 'errors.registration.emailAlreadyInUse';
            }
          } catch {
            // Fallback to generic message if provider detection fails
            errorMessageKey = 'errors.registration.emailAlreadyInUse';
          }
          break;
        case 'auth/weak-password':
          errorMessageKey = 'errors.registration.weakPassword';
          break;
        case 'auth/invalid-email':
          errorMessageKey = 'errors.registration.invalidEmail';
          break;
        case 'auth/operation-not-allowed':
          errorMessageKey = 'errors.registration.operationNotAllowed';
          break;
        case 'auth/network-request-failed':
          errorMessageKey = 'errors.registration.networkError';
          break;
        case 'permission-denied':
          errorMessageKey = 'errors.registration.permissionDenied';
          break;
        default:
          if (code.startsWith('auth/')) {
            errorMessageKey = 'errors.registration.authError';
          }
      }
    }

    return {
      success: false,
      error: errorMessageKey,
      errorCode,
    };
  }
}
